import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '../../hooks/useLogout';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Divider,
    IconButton,
    Collapse,
    Box,
    Tooltip,
    useTheme,
    useMediaQuery,
    Avatar,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Assignment as IncidentsIcon,
    BarChart as AnalyticsIcon,
    ExitToApp as LogoutIcon,
    Person as PersonIcon,
    Menu as MenuIcon,
    KeyboardDoubleArrowLeft as CollapseIcon,
} from '@mui/icons-material';
import { RootState } from '../../types';
import { Logo } from '../Logo';
import NotificationBell from './NotificationBell';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

interface NavItem {
    id: string;
    label: string;
    icon: React.ElementType;
    path: string;
}

const NAV_ITEMS: NavItem[] = [
    { id: 'analytics', label: 'Analytics',    icon: AnalyticsIcon,  path: '/client/analytics' },
    { id: 'tickets',   label: 'My Incidents', icon: IncidentsIcon,  path: '/client/tickets'   },
];

interface ClientLayoutProps {
    children: React.ReactNode;
}

const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useSelector((state: RootState) => state.auth);
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleLogout = () => {
        setAnchorEl(null);
        logout();
    };

    const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

    const initials = [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'Client';

    const tooltipSlotProps = {
        popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, -14] } }],
        },
        tooltip: {
            sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.border.main}`,
                fontSize: '0.8rem',
                fontWeight: 500,
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            },
        },
        arrow: {
            sx: {
                color: theme.palette.background.paper,
                '&::before': { border: `1px solid ${theme.palette.border.main}` },
            },
        },
    };

    const renderNavItem = (item: NavItem) => {
        const active = isActive(item.path);
        const Icon = item.icon;

        const btn = (
            <ListItem key={item.id} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                <ListItemButton
                    onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                    disableRipple
                    sx={{
                        minHeight: 48,
                        px: collapsed ? 0 : 2.5,
                        mx: collapsed ? 0.5 : 1,
                        borderRadius: 1,
                        backgroundColor: 'transparent',
                        color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: theme.palette.interactive.hover,
                            color: theme.palette.text.primary,
                            transform: collapsed ? 'scale(1.1)' : 'translateX(4px)',
                        },
                        '&:active': {
                            transform: collapsed ? 'scale(1.05)' : 'translateX(2px)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: collapsed ? 0 : 3,
                            justifyContent: 'center',
                            color: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            width: collapsed ? '100%' : 'auto',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        <Icon />
                    </ListItemIcon>
                    {!collapsed && (
                        <ListItemText
                            primary={item.label}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontWeight: active ? 700 : 500,
                                    fontSize: '0.9rem',
                                    letterSpacing: '0.02em',
                                    color: active ? theme.palette.text.primary : 'inherit',
                                },
                            }}
                        />
                    )}
                </ListItemButton>
            </ListItem>
        );

        if (collapsed) {
            return (
                <Tooltip key={item.id} title={item.label} placement="right" arrow slotProps={tooltipSlotProps}>
                    {btn}
                </Tooltip>
            );
        }
        return btn;
    };

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: theme.palette.background.default }}>
            {/* Header */}
            <Toolbar
                sx={{
                    px: { xs: 2, sm: 3, md: 4 },
                    justifyContent: collapsed ? 'center' : 'space-between',
                    backgroundColor: 'transparent',
                    minHeight: '72px !important',
                }}
            >
                <Collapse in={!collapsed} timeout={300} orientation="horizontal">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', ml: -1.2, pt: 7, pb: 4 }}>
                        <Logo
                            size={60}
                            primaryColor="oklch(60% 0.22 291)"
                            accentColor="oklch(75% 0.10 291)"
                        />
                    </Box>
                </Collapse>

                {!isMobile && (
                    <IconButton
                        onClick={() => setCollapsed(c => !c)}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            borderRadius: 1,
                            width: 36,
                            height: 36,
                            mr: collapsed ? 0 : -4,
                            mt: collapsed ? 0 : 2.8,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {collapsed ? <MenuIcon /> : <CollapseIcon />}
                    </IconButton>
                )}
            </Toolbar>

            {/* Nav items */}
            <Box sx={{ flexGrow: 1, py: 2 }}>
                <List sx={{ px: 0 }}>
                    {NAV_ITEMS.map(renderNavItem)}
                </List>
            </Box>

            {/* Notification bell */}
            <Box sx={{ pb: 1 }}>
                <NotificationBell collapsed={collapsed} />
            </Box>

            {/* User / footer */}
            <Box sx={{ p: 2 }}>
                {collapsed ? (
                    <Tooltip title={`${fullName} — sign out`} placement="right" arrow slotProps={tooltipSlotProps}>
                        <Box sx={{ textAlign: 'center' }}>
                            <IconButton
                                onClick={handleLogout}
                                size="small"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    borderRadius: 1,
                                    width: 36,
                                    height: 36,
                                    '&:hover': { color: theme.palette.error.main, backgroundColor: theme.palette.interactive.hover },
                                }}
                            >
                                <LogoutIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Tooltip>
                ) : (
                    <Box
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderRadius: 1,
                            p: 1,
                            mx: 1,
                            '&:hover': { backgroundColor: theme.palette.interactive.hover },
                            transition: 'background 0.2s',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                flexShrink: 0,
                            }}
                        >
                            {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                                {fullName}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.primary.main }}>
                                Client
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* User popup menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 1,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        minWidth: 180,
                        '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            mx: 1,
                            '&:hover': { backgroundColor: theme.palette.interactive.hover },
                        },
                    },
                }}
            >
                <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                    </ListItemIcon>
                    <Typography sx={{ fontSize: '0.875rem', color: theme.palette.error.main }}>Sign out</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            {/* Mobile toggle button */}
            {isMobile && (
                <IconButton
                    onClick={() => setMobileOpen(o => !o)}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: 16,
                        zIndex: theme.zIndex.appBar + 1,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.secondary,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText },
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {/* Desktop drawer */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            backgroundColor: theme.palette.background.default,
                            border: 'none',
                            borderRight: `1px solid ${theme.palette.border.light}`,
                            borderRadius: 0,
                            overflowX: 'hidden',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.standard,
                            }),
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Mobile drawer */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            backgroundColor: theme.palette.background.default,
                            border: 'none',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minHeight: '100vh',
                    backgroundColor: theme.palette.background.default,
                    overflow: 'auto',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default ClientLayout;
